import { useLocation, useParams } from "react-router-dom";
import { useEffect, useMemo } from "react";

import BusinessCardError from "../../atoms/BusinessCardError";
import BusinessCardBody from "../../atoms/BusinessCardBody";
import LoadingPage from "../../public/LoadingPage";
import UnknownProcessTypeView from "../error/UnknownProcessTypeView";
import { ProcessData } from "../../../types/BusinessCardTypes";
import {
  getDisplayName,
  mapProcessDataToBusinessCardPayload,
} from "../../../utils/processData";
import { useLanguageDetection } from "../../../hooks/useLanguageDetection";
import { generateBusinessCardHelmet } from "../../../utils/helmetUtils";
import { palette } from "../../../utils/palette";
import { useGetProcessData } from "../../../hooks/entity";
import { useLmcUserView } from "../../../contexts/AuthContext";
import { DefaultImage } from "../../../constants";
import reactManifest from "react-manifest";
import useBrowserInfo from "../../../utils/useBrowserInfo";
import { useSaveBusinessCardStatistics } from "../../../hooks/users";
import { captureBusinessCardAttribution } from "../../../utils/businessCardAttribution";

interface LocationState {
  from?: string;
  processData?: ProcessData;
  cacheTimestamp?: number;
}
const CACHE_TTL = 2 * 60 * 1000;

const BusinessCardApp = () => {
  const info = useBrowserInfo();

  const { processId } = useParams<{ processId: string }>();
  const location = useLocation();
  const { dispatch } = useLmcUserView();

  useEffect(() => {
    captureBusinessCardAttribution(location.search);
  }, [location.search]);

  const navigationState = location.state as LocationState;

  const hasNavigationData = Boolean(navigationState?.processData);

  const isCacheValid = useMemo(() => {
    if (!hasNavigationData || !navigationState?.cacheTimestamp) {
      return false;
    }
    const now = Date.now();
    const cacheAge = now - navigationState.cacheTimestamp;
    return cacheAge < CACHE_TTL;
  }, [hasNavigationData, navigationState?.cacheTimestamp]);

  const cachedProcessData = isCacheValid
    ? navigationState?.processData || null
    : null;

  const {
    data: processData,
    isLoading,
    isError,
  } = useGetProcessData(processId as string, {
    enabled: !isCacheValid,
    onSuccess: (data) => {
      dispatch({
        type: "ENTITY",
        payload: data,
      });
    },
  });

  const { mutate: saveStatistics } = useSaveBusinessCardStatistics();

  const finalProcessData = cachedProcessData || processData;

  const businessCardPayload = useMemo(() => {
    if (!finalProcessData) return null;
    return mapProcessDataToBusinessCardPayload(finalProcessData);
  }, [finalProcessData]);

  const userData = businessCardPayload?.userData;
  const businessCardData = businessCardPayload?.businessCardData;
  const entityData = businessCardPayload?.entityData;
  const settings = businessCardPayload?.settings;

  useEffect(() => {
    const finalData = cachedProcessData || processData;
    if (!finalData) return;

    if (settings?.saveStatistics === true) {
      saveStatistics({
        entity_identification: entityData?.entity_identification || "",
        user_identification: userData?.user_identification,
        event_key: "USER_BUSINESS_CARD_SCANNED",
        data: info,
      });
    }
    dispatch({ type: "ENTITY", payload: finalData });
  }, [cachedProcessData, processData, dispatch]);

  const isValidImage = (url?: string) => !!url && /\.(jpe?g|png)$/i.test(url);

  useEffect(() => {
    if (!userData) return;

    const displayName = getDisplayName(userData) + " - Super User Manager";

    const userImage = isValidImage(userData?.avatar)
      ? userData.avatar
      : isValidImage(entityData?.entity_logo)
      ? entityData?.entity_logo
      : DefaultImage;

    const manifest = {
      name: displayName,
      short_name: displayName,
      start_url: `${window.location.origin}/business-card/${processId}`,
      display: "standalone",
      background_color: palette.black,
      theme_color: palette.accent,
      icons: [
        {
          src: userImage,
          sizes: "192x192",
          purpose: "any maskable",
        },
        {
          src: userImage,
          sizes: "512x512",
          purpose: "any maskable",
        },
      ],
    };

    let placeholder = document.querySelector<HTMLLinkElement>(
      "#manifest-placeholder"
    );
    const createdHere = !placeholder;
    if (!placeholder) {
      placeholder = document.createElement("link");
      placeholder.id = "manifest-placeholder";
      placeholder.rel = "manifest";
      document.head.appendChild(placeholder);
    }
    reactManifest.update(manifest, "#manifest-placeholder");

    return () => {
      if (createdHere) {
        document.querySelector("#manifest-placeholder")?.remove();
      }
    };
  }, [processId, finalProcessData, userData, entityData]);

  useLanguageDetection(finalProcessData);

  if (isLoading && !isCacheValid) {
    return <LoadingPage />;
  }

  if (isError && !isCacheValid) {
    return <UnknownProcessTypeView processType={undefined} />;
  }

  if (
    !finalProcessData ||
    !businessCardPayload ||
    !userData ||
    !businessCardData
  ) {
    return <BusinessCardError />;
  }

  return (
    <>
      {generateBusinessCardHelmet({
        userData: userData,
        businessCardData: businessCardData,
        entityData,
        processId,
      })}

      <BusinessCardBody payload={businessCardPayload} processId={processId} />
    </>
  );
};

export default BusinessCardApp;
